'use client';
import React, { FC, LegacyRef, useEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { Calendar } from 'primereact/calendar';
import { resolveInitialLanguage } from 'features/language/languageConfig';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/ar';
import 'moment/locale/es';
import 'moment/locale/de';
import { addLocale } from 'primereact/api';
interface AppDatePickerProps {
  value?: Date | Date[] | null | string;
  onChange?: (e: string[] | null | string) => void;
  format?: string;
  required?: boolean;
  name?: string;
  label?: string;
  className?: string;
  inputclassName?: string;
  IsDisabled?: boolean;
  view?: 'month' | 'year' | 'date';
  dateFormat?: string;
  numberOfMonths?: number;
  selectionMode?: 'range' | 'multiple' | 'single' | undefined;
  filter?: boolean;
  placeholder?: string;
  withForm?: boolean;
  inputClassName?: string;
  nulable?: boolean;
  ref?: LegacyRef<Calendar> | undefined;
  yearNavigator?: boolean;
  monthNavigator?: boolean;
  viewFormat?: string;
  showClearBtn?: boolean;
}
const DatePicker: FC<AppDatePickerProps> = ({
  value,
  onChange,
  name,
  label,
  IsDisabled,
  className,
  inputclassName,
  view,
  dateFormat = null,
  numberOfMonths = 1,
  selectionMode = 'single',
  placeholder,
  withForm = true,
  inputClassName,
  yearNavigator = false,
  monthNavigator = false,
  ref,
  viewFormat,
  showClearBtn = false,
}) => {
  addLocale('fr', {
    firstDayOfWeek: 1,
    dayNames: [
      'dimanche',
      'lundi',
      'mardi',
      'mercredi',
      'jeudi',
      'vendredi',
      'samedi',
    ],
    dayNamesShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
    dayNamesMin: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
    monthNames: [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre',
    ],
    monthNamesShort: [
      'jan',
      'fév',
      'mar',
      'avr',
      'mai',
      'jun',
      'jul',
      'aoû',
      'sep',
      'oct',
      'nov',
      'déc',
    ],
    today: "Aujourd'hui",
    clear: 'Effacer',
  });
  addLocale('ar', {
    firstDayOfWeek: 0,
    dayNames: [
      'الأحد',
      'الإثنين',
      'الثلاثاء',
      'الأربعاء',
      'الخميس',
      'الجمعة',
      'السبت',
    ],
    dayNamesShort: ['أحد', 'إثن', 'ثلا', 'أربع', 'خمس', 'جمعة', 'سبت'],
    dayNamesMin: ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'],
    monthNames: [
      'يناير',
      'فبراير',
      'مارس',
      'أبريل',
      'مايو',
      'يونيو',
      'يوليو',
      'أغسطس',
      'سبتمبر',
      'أكتوبر',
      'نوفمبر',
      'ديسمبر',
    ],
    monthNamesShort: [
      'ينا',
      'فبرا',
      'مار',
      'أبري',
      'ماي',
      'يون',
      'يول',
      'أغس',
      'سبت',
      'أكت',
      'نوف',
      'ديس',
    ],
    today: 'اليوم',
    clear: 'مسح',
  });
  addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: [
      'domingo',
      'lunes',
      'martes',
      'miércoles',
      'jueves',
      'viernes',
      'sábado',
    ],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ],
    monthNamesShort: [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sep',
      'oct',
      'nov',
      'dic',
    ],
    today: 'Hoy',
    clear: 'Limpiar',
  });
  addLocale('de', {
    firstDayOfWeek: 1,
    dayNames: [
      'Sonntag',
      'Montag',
      'Dienstag',
      'Mittwoch',
      'Donnerstag',
      'Freitag',
      'Samstag',
    ],
    dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
    dayNamesMin: ['S', 'M', 'D', 'M', 'D', 'F', 'S'],
    monthNames: [
      'Januar',
      'Februar',
      'März',
      'April',
      'Mai',
      'Juni',
      'Juli',
      'August',
      'September',
      'Oktober',
      'November',
      'Dezember',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mär',
      'Apr',
      'Mai',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Okt',
      'Nov',
      'Dez',
    ],
    today: 'Heute',
    clear: 'Löschen',
  });
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [meta] = name ? useField(name) : [null];
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const formikField = name ? useField(name) : null;
  const { setFieldValue } = name
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useFormikContext()
    : { setFieldValue: () => {} };
  const [focused, setFocused] = useState(false);
  const [hoverField, setHoverField] = useState(false);
  const [labelWidth, setLabelWidth] = useState(0);
  const [date, setDate] = useState<Date | Date[] | null>(() => {
    if (selectionMode === 'range') {
      if (Array.isArray(value)) {
        return value[0] == null && value[1] == null
          ? null
          : [value[0] ?? null, value[1] ?? null];
      }
      const today = meta?.value
        ? moment(meta.value).toDate()
        : moment().toDate();
      return [today, today];
    }
    if (meta?.value) {
      return moment(meta.value).toDate();
    }
    return value ? moment(value as Date).toDate() : null;
  });
  const labelRef = useRef<HTMLLabelElement>(null);
  useEffect(() => {
    if (date && label) {
      setFocused(true);
    }
    if (labelRef.current) {
      setLabelWidth(labelRef.current.offsetWidth);
    }
  }, [value, date]);
  const handelChange = (e: { value: Date[] | Date }) => {
    setDate(e.value);
    let dateSelected: string | string[] = '';
    if (dateFormat) {
      if (selectionMode === 'multiple' || selectionMode === 'range') {
        dateSelected = (e.value as Date[])?.map((item: Date) =>
          item
            ? moment(new Date(item)).format(dateFormat ?? 'YYYY-MM-DD')
            : null,
        ) as string[];
      } else {
        dateSelected = formatDate(new Date(dateFormat));
      }
    } else {
      if (selectionMode === 'multiple' || selectionMode === 'range') {
        dateSelected = (e.value as Date[]).map((item: Date) =>
          item ? moment(new Date(item)).format('YYYY-MM-DD') : null,
        ) as string[];
      } else {
        dateSelected = formatDate(new Date(e.value as Date));
      }
    }
    if (name) {
      setFieldValue(name, dateSelected);
    } else if (onChange) {
      onChange(dateSelected);
    }
  };
  useEffect(() => {
    if (!meta?.value) {
      if (!value) {
        setDate(null);
      }
    }
  }, [value]);
  const formatDate = (date: Date) => {
    return moment(date).format(dateFormat ?? 'YYYY-MM-DD');
  };

  const handelCleareDate = (): void => {
    setDate(null);
    setFocused(false);
    if (name) {
      setFieldValue(name, null);
    } else if (onChange) {
      onChange(null);
    }
  };
  return (
    <div
      onMouseEnter={() => {
        setHoverField(true);
      }}
      onMouseLeave={() => {
        setHoverField(false);
      }}
      className={` ${withForm && ' minthr-form-outline'}  ${
        formikField?.[1].error && formikField?.[1].touched ? 'error' : ''
      } ${className ? className : ''}`}
    >
      <div className={`${withForm && ' minthr-form-outline'} `}>
        <div
          className={`clear-date ${
            withForm && !placeholder ? 'minthr-form-control' : ''
          } ${focused ? 'active' : ''}`}
        >
          <Calendar
            locale={resolveInitialLanguage()}
            value={date as Date}
            onChange={(e: object) => {
              handelChange(e as { value: Date[] | Date });
            }}
            disabled={IsDisabled}
            name={name}
            dateFormat={viewFormat ?? 'yy/mm/dd'}
            className={inputclassName}
            view={view ?? 'date'}
            numberOfMonths={numberOfMonths}
            selectionMode={selectionMode as 'single' | undefined}
            inputClassName={inputClassName}
            placeholder={placeholder}
            ref={ref}
            yearNavigator={yearNavigator}
            monthNavigator={monthNavigator}
            prevIcon={
              <img src="/icons/arrow.svg" style={{ rotate: '180deg' }} />
            }
            nextIcon={<img src="/icons/arrow.svg" />}
          />
          {showClearBtn && hoverField && date && (
            <a
              className="clear-click"
              onClick={() => {
                handelCleareDate();
              }}
            ></a>
          )}
        </div>
        {label && (
          <label ref={labelRef} className="minthr-form-label">
            {label}
          </label>
        )}
        {label && (
          <div className="minthr-form-notch">
            <div className="minthr-form-notch-leading"></div>
            <div
              className="minthr-form-notch-middle"
              style={{ minWidth: `${labelWidth + 20}px` }}
            ></div>
            <div className="minthr-form-notch-trailing"></div>
          </div>
        )}
      </div>
      {formikField?.[1].touched && formikField?.[1].error ? (
        <div className="minthr-mt-2 text-s" style={{ color: 'red' }}>
          {formikField[1].error}
        </div>
      ) : null}
    </div>
  );
};
export default DatePicker;
